import React, { useEffect, useRef, useState } from "react";
import { BarTask } from "../../types/bar-task";
import { Task } from "../../types/public-types";

export type TaskListProps = {
  nameColumnWidth: number;
  headerHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  rowHeight: number;
  ganttHeight: number;
  scrollY: number;
  locale: string;
  tasks: Task[];
  taskListRef: React.RefObject<HTMLDivElement>;
  horizontalContainerClass?: string;
  selectedTask: BarTask | undefined;
  setSelectedTask: (task: string) => void;
  onExpanderClick: (task: Task) => void;
  onTaskReorder?: (draggedTask: Task, targetTask: Task) => void;
  columnVisibility?: {
    coordinator?: boolean;
    executor?: boolean;
    branch?: boolean;
    name?: boolean;
    start?: boolean;
    end?: boolean;
    progress?: boolean;
  };
  columnOrder?: string[];
  onColumnOrderChange?: (newOrder: string[]) => void;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>, shouldSave?: boolean) => void;
  collapsedChains?: Set<string>;
  onChainToggle?: (chainParentId: string) => void;
  TaskListHeader: React.FC<{
    nameColumnWidth: number;
    headerHeight: number;
    rowWidth: string;
    fontFamily: string;
    fontSize: string;
    columnVisibility?: {
      coordinator?: boolean;
      executor?: boolean;
      branch?: boolean;
      name?: boolean;
      start?: boolean;
      end?: boolean;
      progress?: boolean;
    };
    columnOrder?: string[];
    onColumnOrderChange?: (newOrder: string[]) => void;
    columnWidths?: Record<string, number>;
    onColumnWidthsChange?: (widths: Record<string, number>, shouldSave?: boolean) => void;
  }>;
  TaskListTable: React.FC<{
    nameColumnWidth: number;
    rowHeight: number;
    rowWidth: string;
    fontFamily: string;
    fontSize: string;
    locale: string;
    tasks: Task[];
    selectedTaskId: string;
    setSelectedTask: (taskId: string) => void;
    onExpanderClick: (task: Task) => void;
    onTaskReorder?: (draggedTask: Task, targetTask: Task) => void;
    columnVisibility?: {
      coordinator?: boolean;
      executor?: boolean;
      branch?: boolean;
      name?: boolean;
      start?: boolean;
      end?: boolean;
      progress?: boolean;
    };
    columnOrder?: string[];
    columnWidths?: Record<string, number>;
    collapsedChains?: Set<string>;
    onChainToggle?: (chainParentId: string) => void;
  }>;
};

export const TaskList: React.FC<TaskListProps> = ({
    nameColumnWidth,
  headerHeight,
  fontFamily,
  fontSize,
  rowWidth,
  rowHeight,
  scrollY,
  tasks,
  selectedTask,
  setSelectedTask,
  onExpanderClick,
  onTaskReorder,
  locale,
  ganttHeight,
  taskListRef,
  horizontalContainerClass,
  TaskListHeader,
  TaskListTable,
  columnVisibility,
  columnOrder,
  onColumnOrderChange,
  columnWidths,
  onColumnWidthsChange,
  collapsedChains,
  onChainToggle,
}) => {
  const horizontalContainerRef = useRef<HTMLDivElement>(null);
  const [localColumnWidths, setLocalColumnWidths] = useState<Record<string, number> | undefined>(columnWidths);

  // Update local widths when props change (from saved settings)
  useEffect(() => {
    setLocalColumnWidths(columnWidths);
  }, [columnWidths]);

  useEffect(() => {
    if (horizontalContainerRef.current) {
      horizontalContainerRef.current.scrollTop = scrollY;
    }
  }, [scrollY]);

  // Handle column width changes - update local state immediately, optionally save
  const handleLocalColumnWidthsChange = (widths: Record<string, number>, shouldSave?: boolean) => {
    setLocalColumnWidths(widths);
    if (shouldSave && onColumnWidthsChange) {
      onColumnWidthsChange(widths, shouldSave);
    }
  };

  const headerProps = {
    nameColumnWidth,
    headerHeight,
    fontFamily,
    fontSize,
    rowWidth,
    columnVisibility,
    columnOrder,
    onColumnOrderChange,
    columnWidths: localColumnWidths,
    onColumnWidthsChange: handleLocalColumnWidthsChange,
  };
  const selectedTaskId = selectedTask ? selectedTask.id : "";
  const tableProps = {
    nameColumnWidth,
    rowHeight,
    rowWidth,
    fontFamily,
    fontSize,
    tasks,
    locale,
    selectedTaskId: selectedTaskId,
    setSelectedTask,
    onExpanderClick,
    onTaskReorder,
    columnVisibility,
    columnOrder,
    columnWidths: localColumnWidths,
    collapsedChains,
    onChainToggle,
  };

  return (
    <div ref={taskListRef}>
      <TaskListHeader {...headerProps} />
      <div
        ref={horizontalContainerRef}
        className={horizontalContainerClass}
        style={ganttHeight ? { height: ganttHeight } : {}}
      >
        <TaskListTable {...tableProps} />
      </div>
    </div>
  );
};
